<template>
    <div>
        <beautiful-chat
            ref="chat"
            :participants="participants"
            :titleImageUrl="titleImageUrl"
            :onMessageWasSent="onMessageWasSent"
            :messageList="sortedMessages"
            :newMessagesCount="newMessagesCount"
            :isOpen="isChatOpen"
            :close="closeChat"
            :icons="icons"
            :open="openChat"
            :showEmoji="true"
            :showFile="false"
            :showEdition="true"
            :showDeletion="true"
            :showTypingIndicator="showTypingIndicator"
            :showLauncher="true"
            :showCloseButton="true"
            :colors="colors"
            :alwaysScrollToBottom="alwaysScrollToBottom"
            @scrollToTop="handleScrollToTop"
            :messageStyling="messageStyling"
            @edit="editMessage"
        >
            <!-- <template v-slot:header>
            <img class="sc-header--img" src="/images/icon/booking_lg.png" />
            Chat between {{participants.map(m=>m.name).join(' & ')}}
            </template> -->
            <template v-slot:user-avatar="{ message, user }">
                <div
                    class="chat-user-icon"
                    :style="{ background: getColor(message.author) }"
                    v-if="
                        message.author !== 'me' &&
                        message.type === 'text' &&
                        user &&
                        user.name
                    "
                    :title="user.name"
                >
                    {{ user.name.toUpperCase()[0] }}
                </div>
            </template>

            <template v-slot:text-message-body="{ message, scopedProps }">
                <div
                    :style="{
                        background: getColor(message.author),
                        padding: '5px 20px',
                        'border-radius': '6px',
                    }"
                >
                    {{ scopedProps }}
                    <small>
                        {{ getUserName(message.author) }} &nbsp;
                        {{ message.CreatedAt }}
                    </small>
                    <div>{{ message.data.text }}</div>
                </div>
            </template>
        </beautiful-chat>
    </div>
</template>

<script>
import ChatMixin from '../../mixins/chat-mixin'
import moment from 'moment'
import _ from 'lodash'

export default {
    name: 'stc-chat',

    props: {
        socket: {
            default: () => null,
        },
    },

    data() {
        return {
            channelId: '',
            userId: '',
            icons: {
                open: {
                    img: '/images/icon/chat/logo-no-bg.svg',
                    name: 'default',
                },
                close: {
                    img: '/images/icon/chat/close-icon.png',
                    name: 'default',
                },
                closeSvg: {
                    img: '/images/icon/chat/close.svg',
                    name: 'default',
                },
            }, // the list of all the participant of the conversation. `name` is the user name, `id` is used to establish the author of a message, `imageUrl` is supposed to be the user avatar.
            titleImageUrl: '/images/icon/users_lg.png',
            newMessagesCount: 0,
            isChatOpen: false, // to determine whether the chat window should be open or closed
            showTypingIndicator: '', // when set to a value matching the participant.id it shows the typing indicator for the specific user
            colors: {
                header: {
                    bg: '#34c8b4',
                    text: '#ffffff',
                },
                launcher: {
                    bg: '#394253',
                },
                messageList: {
                    bg: '#ffffff',
                },
                sentMessage: {
                    bg: '#409eff',
                    text: '#ffffff',
                },
                receivedMessage: {
                    bg: '#394253',
                    text: '#ffffff',
                },
                userInput: {
                    bg: '#f4f7f9',
                    text: '#565867',
                },
            },
            alwaysScrollToBottom: true,
            messageStyling: true,
            isPreviousMsgFetchNeeded: true,
            loader: '',
            canSendMessage: false,
        }
    },

    mixins: [ChatMixin],

    created() {
        this.userId = this.$auth.user().UserID

        Events.$on('open-chat', (isOpen, channelId) => {
            this.channelId = channelId
            isOpen ? this.openChat() : this.closeChat()
        })
    },

    mounted() {
        // Based on the input key in send 'is-someone-typing' event
        const keyInTextInput = this.$refs.chat.$el.getElementsByClassName(
            'sc-user-input--text'
        )
        if (keyInTextInput && keyInTextInput.length > 0) {
            keyInTextInput[0].addEventListener('keyup', (event) => {
                if (event.isComposing || event.keyCode === 13) {
                    return
                }
                if (this.timeout) clearTimeout(this.timeout)

                this.timeout = setTimeout(() => {
                    this.socket.emit('is-someone-typing', this.channelId)
                }, 500)
            })
        }
    },

    beforeDestroy() {
        Events.$off('open-chat')
    },

    methods: {
        onMessageWasSent(message) {
            if (this.canSendMessage) {
                const notification = {
                    UserId: this.userId,
                    FirstName: this.$auth.user().FirstName,
                    LastName: this.$auth.user().LastName,
                    ChannelId: this.channelId,
                    ChannelName: this.currentChannel.ChannelName,
                    Data: this.currentWANotificationId,
                    CustomType: 'WANotification',
                    Type: 'CHAT',
                    ShortCode: 'WA',
                    Message: message.data.text || message.data.emoji,
                }

                this.socket.emit('send-chat-message', notification)

                message.CreatedAt = moment().format('YYYY-MM-DD HH:mm:ss')

                this.messageList = [...this.messageList, message]

                this.showTypingIndicator = ''
            }
        },

        async openChat() {
            this.isChatOpen = true
            this.clearMessages()

            await this.fetchChannel(this.channelId, this.userId)
            await this.fetchMessages(this.channelId, this.userId)
            this.canSendMessage = this.isValidParticipant
            this.readMessages(this.channelId)

            this.newMessagesCount = 0

            this.socket.emit('join-room', this.channelId, this.userId)

            this.socket.on('chat-message', (data) => {
                const author =
                    data.UserId === this.userId
                        ? 'me'
                        : `${data.FirstName} ${data.LastName}`
                const message = {
                    type: 'text',
                    author,
                    data: { text: data.Message },
                    CreatedAt: moment(data.CreatedAt).format(
                        'YYYY-MM-DD HH:mm:ss'
                    ),
                }
                this.messageList = [...this.messageList, message]
            })

            this.socket.on('someone-typing', () => {
                this.showTypingIndicator = 'someone'
                setTimeout(() => {
                    this.showTypingIndicator = ''
                }, 3000)
            })

            this.addLoader()
        },

        closeChat() {
            this.clearMessages()
            this.socket.emit('leave-room', this.channelId)
            this.isChatOpen = false
            Events.$emit('show-chat', false)
        },

        clearMessages() {
            this.messageList = []
        },

        editMessage(message) {
            const m = this.messageList.find((m) => m.id === message.id)
            m.isEdited = true
            m.data.text = message.data.text
        },

        getUserName(author) {
            if (author === 'me') return 'Me'
            const filtered = this.participants.filter((it) => it.id === author)
            return filtered && filtered.length > 0 ? filtered[0].name : author
        },

        getColor(author) {
            if (author === 'me') return '#409eff'
            const filtered = this.participants.filter((it) => it.id === author)
            return filtered && filtered.length > 0
                ? filtered[0].color
                : '#394253'
        },

        async handleScrollToTop() {
            if (this.isPreviousMsgFetchNeeded) {
                this.alwaysScrollToBottom = false
                this.loader.style.display = 'block'
                this.isPreviousMsgFetchNeeded = await this.fetchPreviousMessages(
                    this.channelId,
                    this.userId
                )
                this.loader.style.display = 'none'
                this.$nextTick(() => {
                    const element = this.$refs.chat.$el.getElementsByClassName(
                        'sc-message-list'
                    )[0]
                    element.scrollTop = 200
                })
            } else {
                if (this.loader) {
                    this.loader.remove()
                }
            }
        },

        addLoader() {
            const element = this.$refs.chat.$el.getElementsByClassName(
                'sc-message-list'
            )[0]
            let loader = document.createElement('div')
            loader.className = 'loader-wrapper message-loader'
            loader.style.marginTop = '10px'
            element.parentNode.insertBefore(loader, element)
            this.loader = this.$refs.chat.$el.getElementsByClassName(
                'message-loader'
            )[0]
            this.loader.style.display = 'none'
        },
    },
}
</script>
<style >
.sc-launcher,
.sc-open-icon,
.sc-closed-icon {
    right: 180px !important;
}

.sc-chat-window {
    z-index: 999999;
}
.chat-user-icon {
    padding-top: 3px;
    border-radius: 50%;
    color: #ffffff;
    font-size: 15px;
    line-height: 25px;
    text-align: center;
    background: rgb(57, 66, 83);
    width: 25px !important;
    height: 25px !important;
    min-width: 30px;
    min-height: 30px;
    margin: 5px;
    font-weight: bold;
}
.sc-header--img {
    border-radius: 0% !important;
    height: 50px;
    width: 60px;
}
.sc-typing-indicator {
    padding: 2px 15px !important;
    background: rgb(234, 234, 234) !important;
}
.sc-typing-indicator span {
    background-color: #b6b5ba !important;
    width: 5px !important;
    height: 5px !important;
}
.sc-header--title {
    font-size: 16px !important;
    font-weight: bold;
}
.img-msg {
    width: 30px !important;
}
.user-element {
    font-size: 14px !important;
}
.sc-message--text {
    padding: 0 !important;
}
.message-loader {
    font-size: 10px;
    margin: 0px auto;
    text-indent: -9999em;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: #ffffff;
    background: linear-gradient(
        to right,
        #34c7b3 10%,
        rgba(255, 255, 255, 0) 42%
    );
    position: relative;
    animation: load3 1.4s infinite linear;
    transform: translateZ(0);
}

.message-loader:before {
    width: 50%;
    height: 50%;
    background: #34c7b3;
    border-radius: 100% 0 0 0;
    position: absolute;
    top: 0;
    left: 0;
    content: '';
}
.message-loader:after {
    background: #ffffff;
    width: 75%;
    height: 75%;
    border-radius: 50%;
    content: '';
    margin: auto;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
}
@-webkit-keyframes load3 {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
@keyframes load3 {
    0% {
        transform: rotate(0deg);
    }
    100% {
        transform: rotate(360deg);
    }
}
</style>